/**
 * Curl 选项解析器
 * 负责解析 curl 命令中的各种选项
 */

export class CurlOptionParser {
  constructor() {
    // curl 选项映射表
    this.optionMap = {
      // HTTP 方法
      'X': 'request',
      'request': 'request',

      // 请求头
      'H': 'header',
      'header': 'header',

      // 请求数据
      'd': 'data',
      'data': 'data',
      'data-raw': 'data-raw',
      'data-binary': 'data-binary',
      'data-urlencode': 'data-urlencode',

      // 认证
      'u': 'user',
      'user': 'user',
      'basic': 'basic',
      'digest': 'digest',
      'ntlm': 'ntlm',
      'negotiate': 'negotiate',
      'oauth2-bearer': 'oauth2-bearer',

      // Cookie
      'b': 'cookie',
      'cookie': 'cookie',
      'c': 'cookie-jar',
      'cookie-jar': 'cookie-jar',

      // 文件上传
      'F': 'form',
      'form': 'form',
      'form-string': 'form-string',

      // 连接选项
      'x': 'proxy',
      'proxy': 'proxy',
      'proxy-user': 'proxy-user',
      'proxy-basic': 'proxy-basic',

      // SSL/TLS
      'k': 'insecure',
      'insecure': 'insecure',
      'cert': 'cert',
      'key': 'key',
      'cacert': 'cacert',
      'capath': 'capath',

      // 重定向
      'L': 'location',
      'location': 'location',
      'max-redirs': 'max-redirs',

      // 超时
      'connect-timeout': 'connect-timeout',
      'max-time': 'max-time',
      'speed-limit': 'speed-limit',
      'speed-time': 'speed-time',

      // 输出
      'o': 'output',
      'output': 'output',
      'O': 'remote-name',
      'remote-name': 'remote-name',
      's': 'silent',
      'silent': 'silent',
      'v': 'verbose',
      'verbose': 'verbose',

      // 其他
      'A': 'user-agent',
      'user-agent': 'user-agent',
      'e': 'referer',
      'referer': 'referer',
      'compressed': 'compressed',
      'keepalive-time': 'keepalive-time'
    };

    // 选项别名映射
    this.aliases = {
      'request': ['X'],
      'header': ['H'],
      'data': ['d'],
      'user': ['u'],
      'cookie': ['b'],
      'cookie-jar': ['c'],
      'form': ['F'],
      'proxy': ['x'],
      'insecure': ['k'],
      'location': ['L'],
      'output': ['o'],
      'remote-name': ['O'],
      'silent': ['s'],
      'verbose': ['v'],
      'user-agent': ['A'],
      'referer': ['e']
    };
  }

  /**
   * 解析选项
   * @param {Object} token - 选项 token
   * @param {Array} args - 参数 tokens
   * @returns {Object} 解析结果
   */
  parseOption(token, args) {
    const optionName = this.normalizeOption(token.value);
    const canonicalName = this.getCanonicalName(optionName);

    if (!canonicalName) {
      throw new Error(`未知的 curl 选项: ${optionName}`);
    }

    const parser = this.getOptionParser(canonicalName);
    const result = parser.call(this, args, token);

    return {
      option: canonicalName,
      value: result.value,
      consumedTokens: result.consumed,
      original: token.value
    };
  }

  /**
   * 标准化选项名称
   * @param {string} option - 原始选项
   * @returns {string} 标准化后的选项
   */
  normalizeOption(option) {
    // 移除前缀的 '-'
    return option.replace(/^--?/, '');
  }

  /**
   * 获取规范选项名称
   * @param {string} option - 选项名称
   * @returns {string|null} 规范名称
   */
  getCanonicalName(option) {
    return this.optionMap[option] || null;
  }

  /**
   * 获取选项解析器
   * @param {string} option - 选项名称
   * @returns {Function} 解析器函数
   */
  getOptionParser(option) {
    const parsers = {
      // HTTP 方法
      request: this.parseRequestMethod,

      // 请求头
      header: this.parseHeader,

      // 请求数据
      data: this.parseData,
      'data-raw': this.parseDataRaw,
      'data-binary': this.parseDataBinary,
      'data-urlencode': this.parseDataUrlencode,

      // 认证
      user: this.parseUser,
      basic: this.parseBasicAuth,
      digest: this.parseDigestAuth,
      'oauth2-bearer': this.parseBearerAuth,

      // Cookie
      cookie: this.parseCookie,
      'cookie-jar': this.parseCookieJar,

      // 文件上传
      form: this.parseForm,
      'form-string': this.parseFormString,

      // 代理
      proxy: this.parseProxy,
      'proxy-user': this.parseProxyUser,
      'proxy-basic': this.parseProxyBasic,

      // SSL/TLS
      insecure: this.parseInsecure,
      cert: this.parseCert,
      key: this.parseKey,
      cacert: this.parseCaCert,
      capath: this.parseCaPath,

      // 重定向
      location: this.parseLocation,
      'max-redirs': this.parseMaxRedirs,

      // 超时
      'connect-timeout': this.parseConnectTimeout,
      'max-time': this.parseMaxTime,
      'speed-limit': this.parseSpeedLimit,
      'speed-time': this.parseSpeedTime,

      // 输出
      output: this.parseOutput,
      'remote-name': this.parseRemoteName,
      silent: this.parseSilent,
      verbose: this.parseVerbose,

      // 其他
      'user-agent': this.parseUserAgent,
      referer: this.parseReferer,
      compressed: this.parseCompressed,
      'keepalive-time': this.parseKeepaliveTime
    };

    return parsers[option] || this.parseGenericOption;
  }

  /**
   * 解析 HTTP 请求方法
   */
  parseRequestMethod(args, token) {
    if (args.length === 0) {
      throw new Error('请求方法选项需要参数');
    }

    const method = args[0].value.toUpperCase();
    const validMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS', 'CONNECT', 'TRACE'];

    if (!validMethods.includes(method)) {
      throw new Error(`无效的 HTTP 方法: ${method}`);
    }

    return { value: method, consumed: 1 };
  }

  /**
   * 解析请求头
   */
  parseHeader(args, token) {
    if (args.length === 0) {
      throw new Error('请求头选项需要参数');
    }

    const headerString = args[0].value;
    const colonIndex = headerString.indexOf(':');

    if (colonIndex === -1) {
      throw new Error(`无效的请求头格式: ${headerString}`);
    }

    const name = headerString.substring(0, colonIndex).trim();
    const value = headerString.substring(colonIndex + 1).trim();

    return {
      value: { name, value },
      consumed: 1
    };
  }

  /**
   * 解析请求数据
   */
  parseData(args, token) {
    if (args.length === 0) {
      throw new Error('数据选项需要参数');
    }

    const data = args[0].value;
    return {
      value: {
        type: 'data',
        content: data,
        encoding: 'auto'
      },
      consumed: 1
    };
  }

  /**
   * 解析原始数据
   */
  parseDataRaw(args, token) {
    if (args.length === 0) {
      throw new Error('--data-raw 选项需要参数');
    }

    return {
      value: {
        type: 'raw',
        content: args[0].value
      },
      consumed: 1
    };
  }

  /**
   * 解析二进制数据
   */
  parseDataBinary(args, token) {
    if (args.length === 0) {
      throw new Error('--data-binary 选项需要参数');
    }

    return {
      value: {
        type: 'binary',
        content: args[0].value
      },
      consumed: 1
    };
  }

  /**
   * 解析 URL 编码数据
   */
  parseDataUrlencode(args, token) {
    if (args.length === 0) {
      throw new Error('--data-urlencode 选项需要参数');
    }

    return {
      value: {
        type: 'urlencode',
        content: args[0].value
      },
      consumed: 1
    };
  }

  /**
   * 解析用户认证
   */
  parseUser(args, token) {
    if (args.length === 0) {
      throw new Error('用户认证选项需要参数');
    }

    const userPass = args[0].value;
    const colonIndex = userPass.indexOf(':');

    if (colonIndex === -1) {
      return {
        value: {
          type: 'user',
          username: userPass,
          password: ''
        },
        consumed: 1
      };
    }

    return {
      value: {
        type: 'user',
        username: userPass.substring(0, colonIndex),
        password: userPass.substring(colonIndex + 1)
      },
      consumed: 1
    };
  }

  /**
   * 解析基本认证
   */
  parseBasicAuth(args, token) {
    return {
      value: { type: 'basic' },
      consumed: 0
    };
  }

  /**
   * 解析摘要认证
   */
  parseDigestAuth(args, token) {
    return {
      value: { type: 'digest' },
      consumed: 0
    };
  }

  /**
   * 解析 Bearer 认证
   */
  parseBearerAuth(args, token) {
    if (args.length === 0) {
      throw new Error('--oauth2-bearer 选项需要参数');
    }

    return {
      value: {
        type: 'bearer',
        token: args[0].value
      },
      consumed: 1
    };
  }

  /**
   * 解析 Cookie
   */
  parseCookie(args, token) {
    if (args.length === 0) {
      throw new Error('Cookie 选项需要参数');
    }

    const cookieString = args[0].value;
    const cookies = this.parseCookieString(cookieString);

    return {
      value: cookies,
      consumed: 1
    };
  }

  /**
   * 解析 Cookie Jar
   */
  parseCookieJar(args, token) {
    if (args.length === 0) {
      throw new Error('--cookie-jar 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析表单数据
   */
  parseForm(args, token) {
    if (args.length === 0) {
      throw new Error('表单选项需要参数');
    }

    const formString = args[0].value;
    const equalIndex = formString.indexOf('=');

    if (equalIndex === -1) {
      throw new Error(`无效的表单格式: ${formString}`);
    }

    const name = formString.substring(0, equalIndex);
    const value = formString.substring(equalIndex + 1);

    return {
      value: {
        type: 'form',
        name,
        value,
        filename: this.extractFilename(value)
      },
      consumed: 1
    };
  }

  /**
   * 解析表单字符串
   */
  parseFormString(args, token) {
    if (args.length === 0) {
      throw new Error('--form-string 选项需要参数');
    }

    const formString = args[0].value;
    const equalIndex = formString.indexOf('=');

    if (equalIndex === -1) {
      throw new Error(`无效的表单格式: ${formString}`);
    }

    return {
      value: {
        type: 'form-string',
        name: formString.substring(0, equalIndex),
        value: formString.substring(equalIndex + 1)
      },
      consumed: 1
    };
  }

  /**
   * 解析代理
   */
  parseProxy(args, token) {
    if (args.length === 0) {
      throw new Error('代理选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析代理用户
   */
  parseProxyUser(args, token) {
    if (args.length === 0) {
      throw new Error('--proxy-user 选项需要参数');
    }

    const userPass = args[0].value;
    const colonIndex = userPass.indexOf(':');

    return {
      value: {
        username: colonIndex === -1 ? userPass : userPass.substring(0, colonIndex),
        password: colonIndex === -1 ? '' : userPass.substring(colonIndex + 1)
      },
      consumed: 1
    };
  }

  /**
   * 解析代理基本认证
   */
  parseProxyBasic(args, token) {
    return {
      value: { type: 'basic' },
      consumed: 0
    };
  }

  /**
   * 解析不安全 SSL
   */
  parseInsecure(args, token) {
    return {
      value: true,
      consumed: 0
    };
  }

  /**
   * 解析证书
   */
  parseCert(args, token) {
    if (args.length === 0) {
      throw new Error('--cert 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析密钥
   */
  parseKey(args, token) {
    if (args.length === 0) {
      throw new Error('--key 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析 CA 证书
   */
  parseCaCert(args, token) {
    if (args.length === 0) {
      throw new Error('--cacert 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析 CA 路径
   */
  parseCaPath(args, token) {
    if (args.length === 0) {
      throw new Error('--capath 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析位置重定向
   */
  parseLocation(args, token) {
    return {
      value: true,
      consumed: 0
    };
  }

  /**
   * 解析连接超时
   */
  parseConnectTimeout(args, token) {
    if (args.length === 0) {
      throw new Error('--connect-timeout 选项需要参数');
    }

    const timeout = parseFloat(args[0].value);
    if (isNaN(timeout) || timeout < 0) {
      throw new Error(`无效的超时值: ${args[0].value}`);
    }

    return {
      value: timeout,
      consumed: 1
    };
  }

  /**
   * 解析最大时间
   */
  parseMaxTime(args, token) {
    if (args.length === 0) {
      throw new Error('--max-time 选项需要参数');
    }

    const time = parseFloat(args[0].value);
    if (isNaN(time) || time < 0) {
      throw new Error(`无效的时间值: ${args[0].value}`);
    }

    return {
      value: time,
      consumed: 1
    };
  }

  /**
   * 解析最大重定向次数
   */
  parseMaxRedirs(args, token) {
    if (args.length === 0) {
      throw new Error('--max-redirs 选项需要参数');
    }

    const maxRedirs = parseInt(args[0].value, 10);
    if (isNaN(maxRedirs) || maxRedirs < 0) {
      throw new Error(`无效的重定向次数: ${args[0].value}`);
    }

    return {
      value: maxRedirs,
      consumed: 1
    };
  }

  /**
   * 解析速度限制
   */
  parseSpeedLimit(args, token) {
    if (args.length === 0) {
      throw new Error('--speed-limit 选项需要参数');
    }

    return {
      value: parseInt(args[0].value, 10),
      consumed: 1
    };
  }

  /**
   * 解析速度时间
   */
  parseSpeedTime(args, token) {
    if (args.length === 0) {
      throw new Error('--speed-time 选项需要参数');
    }

    return {
      value: parseInt(args[0].value, 10),
      consumed: 1
    };
  }

  /**
   * 解析用户代理
   */
  parseUserAgent(args, token) {
    if (args.length === 0) {
      throw new Error('--user-agent 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析 Referer
   */
  parseReferer(args, token) {
    if (args.length === 0) {
      throw new Error('--referer 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析压缩
   */
  parseCompressed(args, token) {
    return {
      value: true,
      consumed: 0
    };
  }

  /**
   * 解析保持连接时间
   */
  parseKeepaliveTime(args, token) {
    if (args.length === 0) {
      throw new Error('--keepalive-time 选项需要参数');
    }

    return {
      value: parseInt(args[0].value, 10),
      consumed: 1
    };
  }

  /**
   * 解析输出文件
   */
  parseOutput(args, token) {
    if (args.length === 0) {
      throw new Error('--output 选项需要参数');
    }

    return {
      value: args[0].value,
      consumed: 1
    };
  }

  /**
   * 解析远程文件名
   */
  parseRemoteName(args, token) {
    return {
      value: true,
      consumed: 0
    };
  }

  /**
   * 解析静默模式
   */
  parseSilent(args, token) {
    return {
      value: true,
      consumed: 0
    };
  }

  /**
   * 解析详细模式
   */
  parseVerbose(args, token) {
    return {
      value: true,
      consumed: 0
    };
  }

  /**
   * 解析通用选项
   */
  parseGenericOption(args, token) {
    // 对于不识别的选项，返回其值
    return {
      value: args.length > 0 ? args[0].value : true,
      consumed: args.length
    };
  }

  /**
   * 解析 Cookie 字符串
   * @param {string} cookieString - Cookie 字符串
   * @returns {Object} 解析后的 Cookie 对象
   */
  parseCookieString(cookieString) {
    const cookies = {};
    const pairs = cookieString.split(';');

    pairs.forEach(pair => {
      const [key, value] = pair.trim().split('=');
      if (key && value) {
        cookies[key.trim()] = value.trim();
      }
    });

    return cookies;
  }

  /**
   * 从值中提取文件名
   * @param {string} value - 值
   * @returns {string|null} 文件名
   */
  extractFilename(value) {
    // 检查是否为文件上传格式 @filename
    if (value.startsWith('@')) {
      return value.substring(1);
    }
    return null;
  }

  /**
   * 获取选项的别名
   * @param {string} option - 选项名称
   * @returns {Array} 别名数组
   */
  getAliases(option) {
    return this.aliases[option] || [];
  }

  /**
   * 检查选项是否存在
   * @param {string} option - 选项名称
   * @returns {boolean} 是否存在
   */
  hasOption(option) {
    return option in this.optionMap;
  }

  /**
   * 获取所有支持的选项
   * @returns {Array} 选项数组
   */
  getSupportedOptions() {
    return Object.keys(this.optionMap);
  }

  /**
   * 获取选项描述
   * @param {string} option - 选项名称
   * @returns {string} 描述
   */
  getOptionDescription(option) {
    const descriptions = {
      request: '指定 HTTP 请求方法',
      header: '添加请求头',
      data: '发送 POST 数据',
      user: '服务器用户和密码',
      cookie: '发送 Cookie',
      proxy: '使用代理',
      insecure: '允许不安全的 SSL 连接',
      location: '跟随重定向',
      'connect-timeout': '连接超时时间',
      'max-time': '最大传输时间',
      'user-agent': '设置 User-Agent',
      output: '将输出写入文件'
    };

    return descriptions[option] || '未知选项';
  }
}